import type { GridSortModel } from '@mui/x-data-grid-pro';
import { create } from 'zustand';

/**
 * Schema version for a saved grid layout. Lives here because four places write it and one
 * reads it, and they have to agree — a writer stamping an older number means its saves are
 * discarded on the next restore.
 *
 * Bump whenever a grid's columns change in a way a saved layout would fight: a column added
 * anywhere but the end, moved, or removed. Stored prefs from an older version are ignored on
 * restore, so the new definition order wins once and is then saved over.
 *
 * The cost of a bump is that every admin loses their saved column widths and order on every
 * grid. That is a fair trade against the alternative, which is a new column silently landing
 * at the far right for exactly the people who use these tables most.
 *
 * 2 — added the sheet-sync status column to the insurance plans grid.
 * 3 — alerts grid: added the open-alert column, widened Type, and narrowed Last Portal Viewer
 *     to 72 now that it shows initials. Saved widths would have kept the old ones and hidden both.
 */
export const GRID_PREFS_VERSION = 3;

export type GridPrefs = {
  /** Always GRID_PREFS_VERSION at write time. See that constant. */
  v: number;
  updatedAt?: string;
  columns?: {
    columnVisibilityModel?: Record<string, boolean>;
    orderedFields?: string[];
    dimensions?: Record<string, { width?: number; flex?: number; maxWidth?: number; minWidth?: number }>;
  };
  pinnedColumns?: { left?: string[]; right?: string[] };
  sortModel?: GridSortModel;
  /**
   * On/off settings of something that is not a grid but is remembered per admin the same way, such
   * as the Patients KPI's switch for its extra cards. Not tied to GRID_PREFS_VERSION: a bump is
   * about column layouts and should not reset these.
   */
  settings?: Record<string, boolean>;
};

type Store = {
  prefs: Record<string, GridPrefs>;
  hydrated: boolean;
  hydrateFromPreferences: (preferencesJson: string | null | undefined) => void;
  setPrefs: (gridName: string, prefs: GridPrefs) => void;
  clear: () => void;
};

const DATA_GRID_KEY = 'dataGridPreferences';

export const useDataGridPreferencesStore = create<Store>(set => ({
  prefs: {},
  hydrated: false,

  hydrateFromPreferences: preferencesJson => {
    let parsed: Record<string, GridPrefs> = {};
    if (preferencesJson) {
      try {
        const all = JSON.parse(preferencesJson);
        const candidate = all?.[DATA_GRID_KEY];
        if (candidate && typeof candidate === 'object') {
          parsed = candidate;
        }
      } catch {
        console.warn('Failed to parse data grid preferences.');
      }
    }
    set({ prefs: parsed, hydrated: true });
  },

  setPrefs: (gridName, prefs) => set(state => ({ prefs: { ...state.prefs, [gridName]: prefs } })),

  clear: () => set({ prefs: {}, hydrated: false })
}));
